import { CircleHelp } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { ContinueCourseSection } from "./sections/ContinueCourse";
import { DailyCheckInSection } from "./sections/DailyCheckIn";
import { DashboardOverviewTour } from "./DashboardOverviewTour";
import { QuickWinsSection } from "./sections/QuickWins";
import { WelcomeMessageSection } from "./sections/Welcome";
import { useDashboardSummary } from "hooks/useDashboardSummary";
import { PageLoadingState } from "components/layout/PageLoadingState";
import { CourseProgressFlow } from "types";
import { ComingUp } from "./sections/ComingUp";
import { CoursePreviews } from "./sections/CoursePreviews";
import { ProgramSurveyCard } from "./sections/ProgramSurveyCard";
import "./Dashboard.css";

const DEFAULT_COURSE_NAME = "NO COURSE";

export const Dashboard: React.FC = () => {
    const {
        data: dashboardSummary,
        loading: summaryLoading,
        error: dashboardSummaryError,
        refetch: refetchDashboardSummary,
    } = useDashboardSummary();
    const [tutorialLaunchToken, setTutorialLaunchToken] = useState(0);

    const rawCourseProgress = dashboardSummary?.course_progress ?? null;
    const courseProgress = rawCourseProgress?.eligibility?.is_locked ? null : rawCourseProgress;
    const hasLockedCourses = Boolean(rawCourseProgress?.eligibility?.is_locked || dashboardSummary?.assigned_courses?.some((course) => course.eligibility?.is_locked));
    const isCourseProgressUnavailable = Boolean(dashboardSummaryError || dashboardSummary?.course_error);
    const activeCourseName =
        dashboardSummary?.active_course?.name ?? courseProgress?.course?.name ?? DEFAULT_COURSE_NAME;

    const allCourseFlows = useMemo<CourseProgressFlow[] | null>(() => {
        if (isCourseProgressUnavailable) {
            return null;
        }

        if (!courseProgress || !Array.isArray(courseProgress.flows)) {
            return null;
        }
        return courseProgress.flows;
    }, [courseProgress, isCourseProgressUnavailable]);

    const courseName = useMemo(() => {
        return activeCourseName.toUpperCase();
    }, [activeCourseName]);

    const assignedCourses = dashboardSummary?.assigned_courses;
    const lifePlan = assignedCourses?.find((course) => course.name.trim().toLowerCase() === "life plan");
    const streakDays = dashboardSummary?.streak?.streak_days;

    const handleOpenTutorial = useCallback(() => {
        setTutorialLaunchToken((currentToken) => currentToken + 1);
    }, []);

    const content = summaryLoading ? <PageLoadingState /> : (
        <div className="learner-home">
            <div className="home-hero">
                <WelcomeMessageSection />
                <div data-tour-id="dashboard-continue-course" data-testid="dashboard-continue-course-target">
                    <ContinueCourseSection
                        courseFlows={allCourseFlows}
                        courseName={courseName}
                        courseId={isCourseProgressUnavailable ? null : courseProgress?.course?.id ?? null}
                        isProgressUnavailable={isCourseProgressUnavailable}
                        hasLockedCourses={hasLockedCourses}
                        chapters={courseProgress?.chapters}
                        onRetry={refetchDashboardSummary}
                    />
                </div>
            </div>
            <div className="home-stats" aria-label="Your learning at a glance">
                {typeof streakDays === "number" ? <div><strong>{streakDays} {streakDays === 1 ? "day" : "days"}</strong><span>App activity streak</span></div> : null}
                {lifePlan && Number.isFinite(lifePlan.progress_percent) ? <div><strong>{Math.round(lifePlan.progress_percent)}%</strong><span>Life Plan progress</span></div> : null}
                {assignedCourses ? <div><strong>{assignedCourses.length}</strong><span>Assigned courses</span></div> : null}
            </div>
            <ProgramSurveyCard />
            <ComingUp />
            <CoursePreviews />
            <div data-tour-id="dashboard-quick-wins" data-testid="dashboard-quick-wins-target" className="home-quick-wins">
                <QuickWinsSection />
            </div>
            <div data-tour-id="dashboard-daily-check-in" data-testid="dashboard-daily-check-in-target">
                <DailyCheckInSection summary={dashboardSummary?.daily_checkin} unavailable={!dashboardSummary?.daily_checkin} />
            </div>
            <button
                type="button"
                onClick={handleOpenTutorial}
                className="fixed bottom-[calc(env(safe-area-inset-bottom)+5.5rem)] right-4 z-[400] flex h-11 w-11 items-center justify-center rounded-full border border-solid border-white/10 bg-white/40 text-foundation-base-darkbase-dark-500 shadow-elevation-sm transition-colors hover:bg-white backdrop-blur-sm backdrop-brightness-[110%] [-webkit-backdrop-filter:blur(4px)_brightness(100%)] sm:bottom-6 sm:right-6 sm:h-12 sm:w-12"
                aria-label="Open dashboard tutorial"
                data-testid="dashboard-overview-tutorial-button"
            >
                <CircleHelp className="h-5 w-5" />
            </button>
        </div>
    );

    return (
        <>
            {content}
            {/* Keep the loading blocker and tour progress mounted until Home is ready. */}
            <DashboardOverviewTour isActive={!summaryLoading} launchToken={tutorialLaunchToken} />
        </>
    );
};
